import store from '@/store'
import axios from '@/client/axios'
import userApi from '@/api/now/userApi'
import jwtDecode from 'jwt-decode'

import { configureScope } from '@sentry/browser'

// Variable Section
let debug = false
const DEFAULT_API_URL = store.state.env.baseURL
const TOKEN_NAME = store.state.env.token.name
const REFRESH_TOKEN_NAME = store.state.env.token.refreshTokenName
const KAKAO_ID = store.state.env.sns.kakao.appId
const GUEST_ID_NAME = store.state.env.sns.channeltalk.guestIdName

function storeSignin() {
  store.dispatch('auth/signin', {
    info: jwtDecode(getToken().access_token),
    refresh: jwtDecode(getToken().access_token)
  })
}

function getRedirectUri() {
  return location.pathname + location.search
}

const getToken = () => {
  if (localStorage.getItem(TOKEN_NAME) === 'undefined') {
    localStorage.clear()
  }

  return {
    access_token: localStorage.getItem(TOKEN_NAME),
    refresh_token: localStorage.getItem(REFRESH_TOKEN_NAME)
  }
}

function signout() {
  localStorage.removeItem(TOKEN_NAME)
  localStorage.removeItem(REFRESH_TOKEN_NAME)
  store.dispatch('auth/signout')

  localStorage.removeItem(GUEST_ID_NAME)
}

// 값 셋팅
function setSignin(token) {
  if (debug) { console.log('setSignin', token) }
  localStorage.setItem(TOKEN_NAME, token.access_token)
  localStorage.setItem(REFRESH_TOKEN_NAME, token.refresh_token)
  storeSignin()
  return null
}

// 성공시에 호출
function signinSuccess(token) {
  if (debug) { console.log('signinSuccess', token) }
  if (window.location.host === 'now.funda.kr') { window.fbq('track', 'Lead') }
  if (debug) { console.log('signin', token) }
  setSignin(token)
  expireTokenProcess()
  return token
}

function sync() {
  return new Promise(async (resolve, reject) => {
    if (refreshLoading) {
      resolves.push(resolve)
    } else if (refreshLoading === false && store.state.auth.info != null && authService.isTokenExpire()) {
      refreshLoading = true
      resolves.push(resolve)
      try {
        await authService.refresh()
      } catch (e) {
        signout()
      } finally {
        while (resolves.length > 0) {
          let r = resolves.pop()
          r(getToken())
        }
        refreshLoading = false
      }
    } else {
      resolve(getToken().token)
    }
  })
}

// 자동 로그아웃
let autoSignoutTimeout = null
function expireTokenProcess() {
  clearTimeout(autoSignoutTimeout)
  // 자동 타임아웃 시간
  let autoExpTime = store.getters['auth/getRefreshTokenExpireTime'] - Date.now()
  autoSignoutTimeout = setTimeout(_ => {
    authService.signout()
    global.$vm._router.push({ path: '/signout/auto', query: { redirectUri: getRedirectUri() } })
  }, autoExpTime)
}

let refreshLoading = false
let resolves = [] // 토큰 갱신시 블럭걸린 resolve
let authService = {
  isSignin() {
    return store.getters['auth/isSignin']
  },
  isTokenExpire() {
    return store.getters['auth/getTokenExpireTime'] < Date.now()
  },
  isRefreshTokenExpire() {
    return store.getters['auth/getRefreshTokenExpireTime'] < Date.now()
  },
  getToken,
  hasAnyRole(roles) {
    return store.getters['auth/hasAnyRole'](roles)
  },
  getProfile() {
    return store.state.auth.profile
  },
  refresh() {
    return new Promise(async (resolve, reject) => {
      try {
        let { data } = await axios({
          url: `${DEFAULT_API_URL}/auth/refresh-token/`,
          method: 'POST',
          data: {
            token: getToken().refresh_token
          }
        })
        let tokenObj = {
          access_token: data.token,
          refresh_token: data.token,
          fuser: store.state.auth.fuser
        }
        signinSuccess(tokenObj)
        resolve(tokenObj)
      } catch (e) {
        reject(e)
      }
    })
  },
  async syncProfile() {
    if (authService.isSignin() === false) {
      return
    }
    return new Promise(async (resolve, reject) => {
      try {
        let { data } = await userApi.getMe()
        store.dispatch('auth/setProfile', data)
        configureScope((scope) => {
          scope.setUser({ email: data.email })
        })
        resolve(data)
      } catch (e) {
        console.error(JSON.stringify(e))
        // token이 403일때 지우고 재접속을 한다
        if (e && e.status && e.status === 403) {
          signout()
          location.reload(true)
          return
        }
        reject(e)
      }
    })
  },
  signinSuccess,
  signout,
  sync,
  // Get (Kakao)
  getKakaoToken() {
    return new Promise((resolve, reject) => {
      let _resolve = resolve
      let _reject = reject
      if (Kakao && !Kakao.Auth) { Kakao.init(KAKAO_ID) }
      if (!Kakao.Auth) {
        _reject()
        return
      }
      Kakao.Auth.login({
        success(authObj) { _resolve(authObj) },
        fail(err) { _reject(err) },
      })
    })
  },
  async getKakaoCode() {
    try {
      if (Kakao && !Kakao.Auth) { Kakao.init(KAKAO_ID) }
      if (!Kakao.Auth) { return }
      await Kakao.Auth.authorize({ redirectUri: location.origin })
    } catch (e) {
      let url = `${DEFAULT_API_URL}/auth/kakao/redirect/?redirectUri=${encodeURIComponent(location.origin)}`
      location.href = url
    }
  },
  // Signin (Kakao)
  async signinKakaoToken(token) {
    if (debug) { console.log('signinKakaoToken', token) }
    if (!token) { return } // 토큰 체크
    let { data } = await axios({
      url: `${DEFAULT_API_URL}/auth/kakao/`,
      method: 'POST',
      data: { access_token: token.trim() }
    })

    let tokenObj = { access_token: data.token, refresh_token: data.token, fuser: data.user }
    signinSuccess(tokenObj)
    return tokenObj
  },
  // Signin (Kakao)
  async signinAppleToken(token) {
    if (debug) { console.log('signinAppleToken', token) }
    if (!token) { return } // 토큰 체크
    let { data } = await axios({
      url: `${DEFAULT_API_URL}/auth/apple/`,
      method: 'POST',
      data: { access_token: token.trim() }
    })

    let tokenObj = { access_token: data.token, refresh_token: data.token, fuser: data.user }
    signinSuccess(tokenObj)
    return tokenObj
  },
  async signinCode(code) {
    if (debug) { console.log('signinCode', code) }
    // Check Section
    if (!code) { return } // 코드 체크

    // Main Section
    localStorage.setItem('kakaocode', code) // 코드 저장
    let { data } = await axios({
      url: `${DEFAULT_API_URL}/auth/kakao/`,
      method: 'POST',
      data: { code: code.trim() }
    })
    localStorage.removeItem('kakaocode') // 코드 삭제
    let tokenObj = { access_token: data.token, refresh_token: data.token, fuser: data.user }
    signinSuccess(tokenObj)
    return tokenObj
  },
  // UTM
  setUtmCookies(query) {
    let utmParams = ['source', 'medium', 'campaign', 'term', 'content']
    let params = Object.getOwnPropertyNames(query)
    // utm param 하나라도 있는지
    let utms = {}
    params.forEach(param => {
      if (utmParams.some(up => param === 'utm_' + up) === true) {
        utms[param.replace('utm_', '')] = query[param]
      }
    })

    if (JSON.stringify(utms) === '{}') {
      return
    }

    document.cookie = encodeURIComponent('futm') + '=' + encodeURIComponent(JSON.stringify(utms))
    console.log(JSON.parse(JSON.stringify(utms)), 'set') // test
  },
  sendUtmCookies() {
    if (authService.isSignin() === false) {
      return
    }
    let name = 'futm'
    let matches = document.cookie.match(new RegExp(
      '(?:^|; )' + name.replace(/([.$?*|{}()[]\/+^])/g, '\\$1') + '=([^;]*)'
    ))
    let utmObj = matches ? JSON.parse(decodeURIComponent(matches[1])) : null
    if (utmObj == null) {
      return
    }
    return new Promise(async (resolve, reject) => {
      try {
        let { data } = await userApi.postUtmParams(utmObj)
        resolve(data)
      } catch (e) {
        reject(e)
      }
    })
  },
}

// 초기값 세팅
if (getToken().refresh_token != null) {
  storeSignin()
  if (authService.isRefreshTokenExpire()) {
    signout()
  } else {
    expireTokenProcess()
    sync()
  }
}

export default authService
